import { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTechnicianAvailability } from 'hooks/useTechnicianAvailability';
import { type AvailabilityCalendar, type TechnicianAvailability } from 'queries/technician/getTechnicianAvailability';

function toMonthStartStr(d: Dayjs) {
  return d.startOf('month').format('YYYY-MM-01');
}

export function useHardwareReturn(technicianId: number | string, month: Dayjs) {
  const monthStart = toMonthStartStr(month);
  const [availability] = useTechnicianAvailability(technicianId, monthStart);

  // Build indexes of blocked days and blocked slots for quick lookups
  const index = useMemo(() => {
    const blockedDays = new Set<string>();
    const busyByDay: Record<string, string[]> = {};

    if (!availability) return { blockedDays, busyByDay };

    Object.entries(availability as AvailabilityCalendar<TechnicianAvailability>).forEach(([dateISO, slots]) => {
      const times = Object.keys(slots);
      const hasFree = times.some((t) => !['J', 'A'].includes(slots[t]?.status));
      if (!hasFree) blockedDays.add(dateISO);

      const busy = times.filter((t) => ['J', 'A'].includes(slots[t]?.status)).map((t) => t.slice(0, 5));
      if (busy.length) busyByDay[dateISO] = busy;
    });

    return { blockedDays, busyByDay };
  }, [availability]);

  const disabledDate = (current: Dayjs) => index.blockedDays.has(current.format('YYYY-MM-DD'));

  const disabledTime = (current?: Dayjs) => {
    const base = (current ?? dayjs()).startOf('minute');
    const dateKey = base.format('YYYY-MM-DD');

    // non business hours
    const daySlots = (availability as AvailabilityCalendar<TechnicianAvailability>)?.[dateKey] ?? {};
    const baseDisabledHours = Array.from({ length: 24 }, (_, h) => {
      const hh = String(h).padStart(2, '0');
      const has00 = !!daySlots[`${hh}:00:00`];
      const has30 = !!daySlots[`${hh}:30:00`];
      return !has00 && !has30 ? h : -1;
    }).filter((h) => h >= 0);

    // if unable to override disable more slots according to availability
    const dayBlocked = new Set<string>(index.busyByDay?.[dateKey] ?? []);
    const isHalfBlocked = (h: number, m: 0 | 30) =>
      dayBlocked.has(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);

    // mark an hour as fully blocked if both halves are blocked
    const conflictHours: number[] = [];
    for (let h = 0; h < 24; h++) {
      if (isHalfBlocked(h, 0) && isHalfBlocked(h, 30)) conflictHours.push(h);
    }
    const disabledHours = Array.from(new Set([...baseDisabledHours, ...conflictHours])).sort((a, b) => a - b);

    // for available hours, disable only blocked halves
    const disabledMinutes = (selectedHour?: number) => {
      const dis: number[] = [];
      if (typeof selectedHour !== 'number' || disabledHours.includes(selectedHour)) return dis;
      if (isHalfBlocked(selectedHour, 0)) dis.push(0);
      if (isHalfBlocked(selectedHour, 30)) dis.push(30);
      return dis;
    };

    return { disabledHours: () => disabledHours, disabledMinutes, hideDisabledOptions: true };
  };

  return { disabledDate, disabledTime };
}
